<template>
  <v-container fluid class="pa-4">
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" location="top right">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn icon="mdi-close" variant="text" @click="snackbar = false" />
      </template>
    </v-snackbar>

    <!-- Stats row -->
    <v-row class="mb-4">
      <v-col cols="6" sm="3" v-for="stat in stats" :key="stat.label">
        <v-card variant="tonal" :color="stat.color" class="pa-3 text-center" rounded="lg">
          <v-icon :color="stat.color" size="28" class="mb-1">{{ stat.icon }}</v-icon>
          <div class="text-h5 font-weight-bold">{{ stat.value }}</div>
          <div class="text-caption text-medium-emphasis">{{ stat.label }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <!-- ── Registration Form ─────────────────────────── -->
      <v-col cols="12" md="4">
        <v-card variant="outlined" rounded="xl" class="pa-4">
          <div class="d-flex align-center mb-4" style="gap: 10px;">
            <v-avatar color="grey-darken-3" size="36" rounded="lg">
              <v-icon color="white" size="20">mdi-account-plus</v-icon>
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-bold">Register Attendee</div>
              <div class="text-caption text-medium-emphasis">Fill in details and pick an event</div>
            </div>
          </div>

          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="invitation.name"
              label="Full Name"
              :rules="nameRules"
              prepend-inner-icon="mdi-account-outline"
              variant="outlined"
              density="comfortable"
              class="mb-2"
            />
            <v-text-field
              v-model="invitation.email"
              label="Email Address"
              :rules="emailRules"
              prepend-inner-icon="mdi-email-outline"
              variant="outlined"
              density="comfortable"
              class="mb-2"
              type="email"
            />
            <v-select
              v-model="invitation.gender"
              :items="genderOptions"
              label="Gender"
              prepend-inner-icon="mdi-gender-male-female"
              variant="outlined"
              density="comfortable"
              class="mb-2"
            />
            <v-text-field
              v-model="invitation.age"
              label="Age"
              prepend-inner-icon="mdi-calendar-account"
              variant="outlined"
              density="comfortable"
              class="mb-2"
              type="number"
              min="1"
            />
            <v-select
              v-model="invitation.event_id"
              :items="activeEvents"
              item-value="id"
              item-title="name"
              label="Select Event (Active only)"
              prepend-inner-icon="mdi-calendar"
              variant="outlined"
              density="comfortable"
              class="mb-2"
              :rules="[(v) => !!v || 'Event is required']"
            />

            <!-- Capacity indicator for selected event -->
            <template v-if="selectedEventCapacity">
              <div class="mb-3 pa-3 rounded-lg" :class="selectedEventCapacity.bgClass">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="text-caption font-weight-medium">
                    <v-icon size="14" class="mr-1">mdi-account-group</v-icon>
                    Capacity
                  </span>
                  <span class="text-caption font-weight-bold" :class="selectedEventCapacity.textClass">
                    {{ selectedEventCapacity.used }} / {{ selectedEventCapacity.max }} seats
                  </span>
                </div>
                <v-progress-linear
                  :model-value="selectedEventCapacity.pct"
                  :color="selectedEventCapacity.color"
                  height="6"
                  rounded
                  bg-color="grey-lighten-3"
                  class="mb-1"
                />
                <div class="text-caption" :class="selectedEventCapacity.textClass">
                  <v-icon size="13" class="mr-1">{{ selectedEventCapacity.icon }}</v-icon>
                  {{ selectedEventCapacity.message }}
                </div>
              </div>
            </template>

            <v-btn
              @click="sendInvitation"
              color="grey-darken-3"
              block
              rounded="lg"
              :loading="submitting"
              prepend-icon="mdi-send"
            >
              Add &amp; Send Invitation
            </v-btn>
            <v-btn
              @click="resetForm"
              variant="text"
              block
              class="mt-2"
              color="grey"
            >
              Clear Form
            </v-btn>
          </v-form>
        </v-card>
      </v-col>

      <!-- ── Attendee List ─────────────────────────────── -->
      <v-col cols="12" md="8">
        <v-card variant="outlined" rounded="xl">
          <!-- Table toolbar -->
          <div class="pa-4 pb-0">
            <div class="text-subtitle-1 font-weight-bold mb-3">Attendee List</div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="search"
                  label="Search name or email"
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="eventFilter"
                  :items="eventFilterOptions"
                  item-value="id"
                  item-title="name"
                  label="Filter by event"
                  prepend-inner-icon="mdi-filter-outline"
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                />
              </v-col>
            </v-row>
          </div>

          <v-data-table
            :headers="headers"
            :items="filteredInvitations"
            item-key="id"
            :items-per-page="8"
            :search="search"
            density="compact"
            class="mt-2"
          >
            <!-- QR Code thumbnail -->
            <template v-slot:[`item.qr_code`]="{ item }">
              <v-avatar
                size="44"
                rounded="sm"
                style="cursor:pointer; border: 1px solid #e0e0e0;"
                @click="qrItem = item; qrDialog = true"
              >
                <v-img
                  v-if="item.qr_code"
                  :src="item.qr_code"
                  cover
                />
                <v-icon v-else color="grey">mdi-qrcode</v-icon>
              </v-avatar>
            </template>

            <!-- Checked-in status -->
            <template v-slot:[`item.checked_in`]="{ item }">
              <v-chip
                :color="item.checked_in == 1 ? 'success' : 'grey'"
                size="x-small"
                variant="tonal"
                :prepend-icon="item.checked_in == 1 ? 'mdi-check-circle' : 'mdi-clock-outline'"
              >
                {{ item.checked_in == 1 ? 'Checked In' : 'Pending' }}
              </v-chip>
            </template>

            <!-- Actions -->
            <template v-slot:[`item.actions`]="{ item }">
              <div class="d-flex align-center" style="gap: 2px;">
                <v-tooltip text="Download PDF" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-download" size="x-small" color="grey-darken-3" variant="tonal" @click="downloadOrShareInvitation(item, false)" />
                  </template>
                </v-tooltip>
                <v-tooltip text="Email invitation" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-email-fast-outline" size="x-small" color="blue" variant="tonal" @click="downloadOrShareInvitation(item, true)" />
                  </template>
                </v-tooltip>
                <v-tooltip text="Regenerate QR" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-qrcode-scan" size="x-small" color="purple" variant="tonal" @click="generateQrCode(item)" />
                  </template>
                </v-tooltip>
                <v-tooltip text="Edit" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-pencil" size="x-small" color="orange" variant="tonal" @click="openEditDialog(item)" />
                  </template>
                </v-tooltip>
                <v-tooltip text="Delete" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-delete" size="x-small" color="red" variant="tonal" @click="deleteInvitation(item.id)" />
                  </template>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- ── QR Code Dialog ──────────────────────────────────── -->
    <v-dialog v-model="qrDialog" max-width="360">
      <v-card rounded="xl" class="pa-4 text-center">
        <v-card-title class="pa-0 mb-1 text-subtitle-1 font-weight-bold">
          {{ qrItem && qrItem.name }}
        </v-card-title>
        <div class="text-caption text-medium-emphasis mb-3">
          {{ qrItem && qrItem.event_name }}
        </div>
        <v-img
          v-if="qrItem && qrItem.qr_code"
          :src="qrItem.qr_code"
          width="260"
          height="260"
          class="mx-auto rounded-lg mb-3"
          style="border: 1px solid #e0e0e0;"
        />
        <div v-else class="py-8 text-medium-emphasis">
          <v-icon size="64" class="mb-2">mdi-qrcode-remove</v-icon>
          <div class="text-caption">No QR code available</div>
        </div>
        <v-card-actions class="pa-0 justify-center" style="gap: 8px;">
          <v-btn
            v-if="qrItem && qrItem.qr_code"
            color="grey-darken-3"
            variant="tonal"
            prepend-icon="mdi-download"
            rounded="lg"
            @click="downloadQr(qrItem)"
          >
            Download
          </v-btn>
          <v-btn variant="text" @click="qrDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Edit Dialog ───────────────────────────────────── -->
    <v-dialog v-model="editDialog" max-width="520px">
      <v-card rounded="xl" class="pa-2">
        <v-card-title class="text-subtitle-1 font-weight-bold pa-4 pb-2">
          <v-icon class="mr-2">mdi-account-edit</v-icon>
          Edit Attendee
        </v-card-title>

        <v-card-text class="pt-2">
          <v-text-field
            v-model="editedAttendee.name"
            label="Full Name"
            prepend-inner-icon="mdi-account-outline"
            variant="outlined"
            density="comfortable"
            class="mb-2"
          />
          <v-text-field
            v-model="editedAttendee.email"
            label="Email Address"
            prepend-inner-icon="mdi-email-outline"
            variant="outlined"
            density="comfortable"
            class="mb-2"
          />
          <v-select
            v-model="editedAttendee.gender"
            :items="genderOptions"
            label="Gender"
            prepend-inner-icon="mdi-gender-male-female"
            variant="outlined"
            density="comfortable"
            class="mb-2"
          />
          <v-select
            v-model="editedAttendee.event_id"
            :items="activeEvents"
            item-value="id"
            item-title="name"
            label="Event (Active only)"
            prepend-inner-icon="mdi-calendar"
            variant="outlined"
            density="comfortable"
            class="mb-2"
          />
          <v-checkbox
            v-model="editedAttendee.checked_in"
            :true-value="1"
            :false-value="0"
            color="success"
            label="Mark as checked in"
            density="compact"
            hide-details
          />
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="editDialog = false">Cancel</v-btn>
          <v-btn color="grey-darken-3" variant="flat" rounded="lg" @click="updateAttendee" prepend-icon="mdi-content-save">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";

export default {
  data() {
    return {
      // UI state
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      valid: false,
      submitting: false,
      editDialog: false,
      search: "",
      eventFilter: null,

      // Form
      invitation: { name: "", email: "", event_id: null, age: null, gender: "" },

      // Edit
      editedAttendee: { id: "", name: "", email: "", checked_in: 0, checkin_time: "", event_id: null, gender: "" },

      // Data
      invitations: [],
      events: [],

      // Constants
      genderOptions: ["Male", "Female"],
      nameRules:  [(v) => !!v || "Name is required"],
      emailRules: [
        (v) => !!v || "Email is required",
        (v) => /.+@.+\..+/.test(v) || "Must be a valid email",
      ],

      headers: [
        { title: "QR Code",    value: "qr_code",     sortable: false },
        { title: "Name",       value: "name",         sortable: true  },
        { title: "Email",      value: "email",         sortable: false },
        { title: "Event",      value: "event_name",   sortable: true  },
        { title: "Gender",     value: "gender",        sortable: true  },
        { title: "Status",     value: "checked_in",   sortable: true  },
        { title: "Actions",    value: "actions",       sortable: false },
      ],
      qrDialog: false,
      qrItem: null,
    };
  },

  computed: {
    eventFilterOptions() {
      return [{ id: null, name: "All Events" }, ...this.events];
    },
    filteredInvitations() {
      if (!this.eventFilter) return this.invitations;
      return this.invitations.filter((i) => i.event_id == this.eventFilter);
    },
    activeEvents() {
      return this.events.filter((e) => e.status == 1);
    },
    selectedEventCapacity() {
      if (!this.invitation.event_id) return null;
      const ev = this.events.find((e) => e.id == this.invitation.event_id);
      if (!ev || !ev.max_invitations) return null;
      const used = ev.attendee_count || 0;
      const max  = ev.max_invitations;
      const pct  = Math.min(100, Math.round((used / max) * 100));
      const remaining = max - used;
      let color     = "green";
      let textClass = "text-green";
      let bgClass   = "bg-green-lighten-5";
      let icon      = "mdi-check-circle-outline";
      let message   = `${remaining} spots remaining`;
      if (pct >= 100) {
        color = "red"; textClass = "text-red"; bgClass = "bg-red-lighten-5";
        icon  = "mdi-alert-circle"; message = "Event is full — no spots left";
      } else if (pct >= 90) {
        color = "red"; textClass = "text-red"; bgClass = "bg-red-lighten-5";
        icon  = "mdi-alert"; message = `Almost full — only ${remaining} spot${remaining === 1 ? "" : "s"} left!`;
      } else if (pct >= 60) {
        color = "orange"; textClass = "text-orange"; bgClass = "bg-orange-lighten-5";
        icon  = "mdi-alert-outline"; message = `${remaining} spots remaining`;
      }
      return { used, max, pct, color, textClass, bgClass, icon, message };
    },
    stats() {
      const total     = this.invitations.length;
      const checkedIn = this.invitations.filter((i) => i.checked_in == 1).length;
      const pending   = total - checkedIn;
      const eventCount = new Set(this.invitations.map((i) => i.event_id)).size;
      return [
        { label: "Total Attendees", value: total,      icon: "mdi-account-group",             color: "blue"   },
        { label: "Checked In",      value: checkedIn,  icon: "mdi-check-circle-outline",       color: "green"  },
        { label: "Pending",         value: pending,    icon: "mdi-clock-outline",              color: "orange" },
        { label: "Events",          value: eventCount, icon: "mdi-calendar-multiple",          color: "purple" },
      ];
    },
  },

  mounted() {
    this.fetchEvents();
    this.fetchInvitations();
  },

  methods: {
    notify(text, color = "success") {
      this.snackbarText  = text;
      this.snackbarColor = color;
      this.snackbar      = true;
    },

    resetForm() {
      this.invitation = { name: "", email: "", event_id: null, age: null, gender: "" };
      this.$refs.form?.resetValidation();
    },

    async fetchEvents() {
      try {
        const response = await axios.get("/api/events");
        this.events = response.data;
      } catch (e) {
        console.error("Error fetching events:", e);
      }
    },

    async fetchInvitations() {
      try {
        const response = await axios.get("/api/attendees");
        this.invitations = response.data.map((inv) => ({
          ...inv,
          event_image:  this.getEventProp(inv.event_id, "image"),
          event_image2: this.getEventProp(inv.event_id, "image2"),
          description:  this.getEventProp(inv.event_id, "description"),
        }));
      } catch (e) {
        console.error("Error fetching attendees:", e);
      }
    },

    downloadQr(item) {
      const link     = document.createElement("a");
      link.href      = item.qr_code;
      link.download  = `QR-${item.name.replace(/\s+/g, "_")}.png`;
      link.click();
    },

    getEventProp(event_id, prop) {
      const event = this.events.find((e) => e.id == event_id);
      return event ? event[prop] : null;
    },

    async sendInvitation() {
      const ok = await this.$refs.form.validate();
      if (!ok.valid) return;

      this.submitting = true;
      try {
        await axios.post("/api/register", this.invitation);

        // Enrich with event data for PDF generation
        const eventData = this.events.find((e) => e.id == this.invitation.event_id) || {};
        const enriched  = {
          ...this.invitation,
          event_name:   eventData.name        || "",
          event_image:  eventData.image       || null,
          event_image2: eventData.image2      || null,
          description:  eventData.description || "",
        };

        this.notify("Attendee registered! Sending invitation email…");
        await this.fetchInvitations();
        this.resetForm();

        // Auto-send invitation email
        this.downloadOrShareInvitation(enriched, true);
      } catch (err) {
        this.notify(
          "Registration failed: " + (err.response?.data?.error || err.message),
          "red"
        );
      } finally {
        this.submitting = false;
      }
    },

    openEditDialog(item) {
      this.editedAttendee = { ...item };
      this.editDialog = true;
    },

    async updateAttendee() {
      try {
        await axios.put(`/api/attendees/${this.editedAttendee.id}`, this.editedAttendee);
        this.editDialog = false;
        this.notify("Attendee updated successfully.");
        await this.fetchInvitations();
      } catch (e) {
        this.notify("Update failed.", "red");
      }
    },

    async deleteInvitation(id) {
      if (!confirm("Delete this attendee?")) return;
      try {
        await axios.delete(`/api/attendees/delete/${id}`);
        this.notify("Attendee deleted.");
        await this.fetchInvitations();
      } catch (e) {
        this.notify("Delete failed.", "red");
      }
    },

    async generateQrCode(invitation) {
      try {
        const qrCodeUrl = await QRCode.toDataURL(
          `${invitation.event_id}*${invitation.name}*${invitation.email}`
        );
        await axios.patch(`/api/attendees/${invitation.id}`, { qrCodeUrl });
        this.notify("QR code regenerated.");
        await this.fetchInvitations();
      } catch (e) {
        this.notify("QR generation failed.", "red");
      }
    },

    async downloadOrShareInvitation(invite, share) {
      const doc        = new jsPDF();
      const pageWidth  = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const qrBoxWidth  = 70;
      const qrBoxHeight = 80;
      const qrBoxX      = (pageWidth - qrBoxWidth) / 2;
      const qrBoxY      = 140;
      const qrSize      = 50;
      const qrX         = qrBoxX + (qrBoxWidth - qrSize) / 2;
      const qrY         = qrBoxY + 10;

      const qrCodeDataUrl = await QRCode.toDataURL(
        `${invite.event_id}*${invite.name}*${invite.email}`
      );

      // Watermark
      doc.saveGraphicsState();
      doc.setTextColor(230, 230, 230);
      doc.setFontSize(50);
      doc.setFont("helvetica", "bold");
      doc.text("Sunrise Events", pageWidth / 2, pageHeight / 2 + 50, { align: "center", angle: 45 });
      doc.restoreGraphicsState();

      // Border
      doc.setDrawColor("#000000");
      doc.setLineDashPattern([4, 2], 0);
      doc.setLineWidth(0.8);
      doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
      doc.setLineDashPattern([], 0);

      // Header bar
      doc.setFillColor("#000000");
      doc.rect(10, 10, pageWidth - 20, 30, "F");
      doc.setTextColor("#FFFFFF");
      doc.setFontSize(18);

      if (invite.event_image) {
        try {
          doc.addImage(
            "/uploads/" + invite.event_image,
            "PNG", 15, 12, qrSize / 2, qrSize / 2
          );
        } catch (_) { /* image optional */ }
      }
      if (invite.event_image2) {
        try {
          doc.addImage(
            "/uploads/" + invite.event_image2,
            "PNG", pageWidth - 15 - qrSize / 2, 12, qrSize / 2, qrSize / 2
          );
        } catch (_) { /* image optional */ }
      }

      doc.text(`${invite.event_name || "Event"} — You're Invited!`, pageWidth / 2, 28, { align: "center" });

      // Invitee details
      doc.setFontSize(14);
      doc.setTextColor("#000000");
      doc.text(`Name: ${invite.name}`,        20, 55);
      doc.text(`Email: ${invite.email}`,       20, 65);
      doc.text(`Event ID: ${invite.event_id}`, 20, 75);

      // Description
      doc.setFontSize(12);
      doc.setTextColor("#333333");
      doc.text(invite.description || "", 20, 90, { maxWidth: pageWidth - 40 });

      // QR box
      doc.setDrawColor("#888888");
      doc.setLineWidth(0.5);
      doc.rect(qrBoxX, qrBoxY, qrBoxWidth, qrBoxHeight);
      doc.addImage(qrCodeDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

      doc.setFontSize(11);
      doc.setTextColor("#333333");
      doc.text("Present this code at the entrance for fast check-in.", pageWidth / 2, qrBoxY + qrBoxHeight + 30, { align: "center" });

      // Footer bar
      doc.setFillColor("#000000");
      doc.rect(10, pageHeight - 30, pageWidth - 20, 20, "F");
      doc.setTextColor("#FFFFFF");
      doc.setFontSize(10);
      doc.text("Sunrise Events · © 2025", pageWidth / 2, pageHeight - 17, { align: "center" });

      if (!share) {
        doc.save(`${invite.event_name || "invitation"}-${invite.name}.pdf`);
      } else {
        const base64Content = doc.output("datauristring").split(",")[1];
        axios.post("/api/send-invitation-email", {
          pdfBase64:  base64Content,
          fileName:   `${invite.event_name || "invitation"}-${invite.name}.pdf`,
          email:      invite.email,
          name:       invite.name,
          event_name: invite.event_name,
        })
          .then(() => this.notify("Invitation emailed to " + invite.email))
          .catch(() => this.notify("Email could not be sent.", "red"));
      }
    },
  },
};
</script>
